import { Text } from '@thanx/uikit/text'
import { useHashTab } from 'components/Tabs/useHashTab'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { buildTranslate } from 'locales'
import React from 'react'
import { Container } from 'react-bootstrap-five'
import Helmet from 'react-helmet'
import { history } from 'store'

import useCurrentMerchantUser from 'hooks/useCurrentMerchantUser'
import useFlag from 'hooks/useFlag'
import { useAbility } from 'utilities/ability'
import CampaignPerformanceTabs, { Tabs } from './CampaignPerformanceTabs'
import EmailEngagementByCampaignTypeTab from './EmailEngagementByCampaignTypeTab'
import EmailEngagementOverview from './EmailEngagementOverview'
import RedemptionRevenueTab from './RedemptionRevenueTab'

const t = buildTranslate('thanx_campaigns.performance')

const CampaignPerformanceReport = () => {
  const isEmailCampaignByTypeReportEnabled = useFlag(
    'mx-email-engagement-by-campaign-looker-report',
    false
  )
  const isPromoteInsightsLabReportsEnabled = useFlag(
    'mx-promote-insights-lab-reports',
    false
  )
  const merchant = useCurrentMerchant()
  const merchantUser = useCurrentMerchantUser()
  const ability = useAbility()
  const visibleTabs = isPromoteInsightsLabReportsEnabled
    ? [
        Tabs.REDEMPTION_REVENUE,
        Tabs.EMAIL_ENGAGEMENT_OVERVIEW,
        Tabs.EMAIL_ENGAGEMENT_BY_CAMPAIGN_TYPE,
      ]
    : [Tabs.EMAIL_ENGAGEMENT_OVERVIEW, Tabs.EMAIL_ENGAGEMENT_BY_CAMPAIGN_TYPE]

  // `useFlag` reads false both when the flag is off and while LaunchDarkly is
  // still evaluating, so the default tab changes once the real value lands.
  // useHashTab keys its effect on the visible set and the fallback, so that
  // late arrival re-resolves instead of stranding a flag-on merchant on Email
  // engagement overview.
  const defaultTab = isPromoteInsightsLabReportsEnabled
    ? Tabs.REDEMPTION_REVENUE
    : Tabs.EMAIL_ENGAGEMENT_OVERVIEW

  const [activeTab, setActiveTab] = useHashTab(visibleTabs, defaultTab)

  const handleSwitchTab = (tab: Tabs) => {
    history.replace({ ...history.location, hash: tab })
    setActiveTab(tab)
  }

  const userIsAdmin = ability.can('access', 'CampaignPerformanceReport')

  const emailEngagementOverviewPath = 'dashboards/110'
  const emailEngagementByCampaignTypePath = 'dashboards/269'
  const emailEngagementOverviewHeight = 1330
  const emailEngagementByCampaignTypeHeight = 1450

  if (!merchant || !merchantUser) return null

  const emailEngagementOverviewContent = (
    <EmailEngagementOverview
      userIsAdmin={userIsAdmin}
      merchant={merchant}
      emailEngagementOverviewPath={emailEngagementOverviewPath}
      emailEngagementOverviewHeight={emailEngagementOverviewHeight}
      titleKey={
        isPromoteInsightsLabReportsEnabled
          ? 'campaignEmailEngagement.title'
          : 'emailEngagement.title'
      }
    />
  )

  const tabContent = (
    <CampaignPerformanceTabs
      activeTab={activeTab}
      setActiveTab={handleSwitchTab}
      isPromoteInsightsLabReportsEnabled={isPromoteInsightsLabReportsEnabled}
      redemptionRevenueContent={<RedemptionRevenueTab />}
      emailEngagementOverviewContent={emailEngagementOverviewContent}
      emailEngagementByCampaignTypeContent={
        <EmailEngagementByCampaignTypeTab />
      }
      userIsAdmin={userIsAdmin}
      merchant={merchant}
      emailEngagementByCampaignTypePath={emailEngagementByCampaignTypePath}
      emailEngagementByCampaignTypeHeight={emailEngagementByCampaignTypeHeight}
    />
  )

  const emailCampiagnContent =
    isPromoteInsightsLabReportsEnabled || isEmailCampaignByTypeReportEnabled
      ? tabContent
      : emailEngagementOverviewContent

  return (
    <div className="w-100 h-100 pt-m pb-xxl px-0 white-bkg border-bottom-1 grey-20-border">
      <Container className="pt-l pb-xl px-0">
        <div className="d-flex justify-content-between">
          <Text.Header2 className="m-0 mr-xl">{t('pageTitle')}</Text.Header2>
        </div>
      </Container>
      <Helmet>
        <title>{t('pageTitle')}</title>
      </Helmet>
      {emailCampiagnContent}
    </div>
  )
}

export default CampaignPerformanceReport
